import { startCase } from 'lodash';
import {
    IconButton,
    Menu,
    MenuContent,
    MenuGroup,
    MenuRadioGroup,
    MenuRadioItem,
    MenuSeparator,
    MenuSub,
    MenuSubContent,
    MenuTrigger,
    MenuItem,
    MenuSubTrigger,
} from '@cad-arc';
import { QueueDisplayDensityEnum, QueueDisplayDensityEnumType } from '@mark43/cad-api';
import { observer } from 'mobx-react-lite';
import { UnitQueueStore } from '../../unit-queues/store/unit-queue-store';
import { useQueueStore } from '../context/use-queue-store';
import { EVENT_UPDATE_NOTIFICATION_MODE } from '../../panels/constants';

function ShiftedText(props: React.HTMLAttributes<HTMLSpanElement>) {
    return (
        <span
            {...props}
            style={{
                marginLeft: 22,
                ...props.style,
            }}
        >
            {props.children}
        </span>
    );
}

export const QueueOtherOptionsMenu = observer(function QueueOtherOptionsMenu({
    onEditColumns,
}: {
    onEditColumns: () => void;
}) {
    const queueStore = useQueueStore() as UnitQueueStore;

    const showPinBackupToPrimaryMenuItem =
        queueStore.workspacePanel.queue.dataEntityType === 'UNIT' &&
        [true, undefined].includes(queueStore.workspacePanel.queue.assignmentStatus);
    const showEventUpdatesMenuItem = ['CAD_AGENCY_EVENT', 'UNIT'].includes(
        queueStore.workspacePanel.queue.dataEntityType
    );
    const showFullRowUnitTimerUpdatesMenuItem =
        queueStore.workspacePanel.queue.dataEntityType === 'UNIT';
    const showNotificationsMenuGroup =
        showEventUpdatesMenuItem || showFullRowUnitTimerUpdatesMenuItem;

    return (
        <Menu>
            <MenuTrigger asChild>
                <IconButton
                    data-testid="other-options-menu"
                    variant="ghost"
                    icon="MoreActions"
                    aria-label="more actions"
                />
            </MenuTrigger>
            <MenuContent>
                <MenuGroup title="View">
                    <MenuSub>
                        <MenuSubTrigger>
                            <ShiftedText>Display Density</ShiftedText>
                        </MenuSubTrigger>
                        <MenuSubContent>
                            <MenuRadioGroup
                                value={queueStore.workspacePanel.queue.displayDensity}
                                onValueChange={(value) =>
                                    queueStore.setDisplayDensity(
                                        value as QueueDisplayDensityEnumType
                                    )
                                }
                            >
                                {Object.values(QueueDisplayDensityEnum).map((density) => (
                                    <MenuRadioItem key={density.name} value={density.name}>
                                        {startCase(density.name.toLowerCase())}
                                    </MenuRadioItem>
                                ))}
                            </MenuRadioGroup>
                        </MenuSubContent>
                    </MenuSub>
                    <MenuItem onSelect={onEditColumns}>
                        <ShiftedText>Edit Columns</ShiftedText>
                    </MenuItem>
                    {showPinBackupToPrimaryMenuItem ? (
                        <MenuRadioGroup
                            onValueChange={() => queueStore.togglePinToPrimary()}
                            value={
                                queueStore.workspacePanel.queue.pinBackupsToPrimaryUnits ? 'Y' : 'N'
                            }
                        >
                            <MenuRadioItem value="Y">Pin Back-up to Primary</MenuRadioItem>
                        </MenuRadioGroup>
                    ) : undefined}
                </MenuGroup>

                {showNotificationsMenuGroup ? (
                    <>
                        <MenuSeparator />
                        <MenuGroup title="Notifications">
                            {showEventUpdatesMenuItem ? (
                                <MenuSub>
                                    <MenuSubTrigger>
                                        <ShiftedText>Event Updates</ShiftedText>
                                    </MenuSubTrigger>
                                    <MenuSubContent>
                                        <MenuRadioGroup
                                            onValueChange={(value) =>
                                                queueStore.switchEventUpdatesNotificationMode(
                                                    value as keyof typeof EVENT_UPDATE_NOTIFICATION_MODE
                                                )
                                            }
                                            value={
                                                queueStore.workspacePanel.queue
                                                    .currentEventUpdatesNotificationMode
                                            }
                                        >
                                            <MenuRadioItem
                                                value={EVENT_UPDATE_NOTIFICATION_MODE.NONE}
                                            >
                                                None
                                            </MenuRadioItem>
                                            <MenuRadioItem
                                                value={EVENT_UPDATE_NOTIFICATION_MODE.BADGE}
                                            >
                                                Badge Updates
                                            </MenuRadioItem>
                                            <MenuRadioItem
                                                value={EVENT_UPDATE_NOTIFICATION_MODE.FULL_ROW}
                                            >
                                                Full Row Updates
                                            </MenuRadioItem>
                                        </MenuRadioGroup>
                                    </MenuSubContent>
                                </MenuSub>
                            ) : undefined}
                            {showFullRowUnitTimerUpdatesMenuItem ? (
                                <MenuRadioGroup
                                    onValueChange={() =>
                                        queueStore.toggleShowFullRowUnitTimerUpdates()
                                    }
                                    value={
                                        queueStore.workspacePanel.queue.fullRowUnitTimerUpdates
                                            ? 'Y'
                                            : 'N'
                                    }
                                >
                                    <MenuRadioItem value="Y">
                                        Full Row Unit Timer Updates
                                    </MenuRadioItem>
                                </MenuRadioGroup>
                            ) : undefined}
                        </MenuGroup>
                    </>
                ) : undefined}
            </MenuContent>
        </Menu>
    );
});
